import { computed, readonly, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import type {
  AuthSession,
  SessionAccess,
  SessionUser,
} from '@/types/domain';

const user = ref<SessionUser | null>(null);
const access = ref<SessionAccess | null>(null);
const ready = ref(false);
let pendingSession: Promise<boolean> | null = null;

function applySession(session: AuthSession): void {
  user.value = session.user;
  access.value = session.access;
  ready.value = true;
}

export function clearSession(): void {
  user.value = null;
  access.value = null;
  ready.value = true;
}

export async function ensureSession(force = false): Promise<boolean> {
  if (!force && ready.value) return Boolean(user.value);
  if (pendingSession) return pendingSession;
  pendingSession = eqsApi.session()
    .then((session) => {
      applySession(session);
      return true;
    })
    .catch(() => {
      clearSession();
      return false;
    })
    .finally(() => {
      pendingSession = null;
    });
  return pendingSession;
}

export function useSession() {
  async function login(username: string, password: string): Promise<AuthSession> {
    const session = await eqsApi.login(username, password);
    applySession(session);
    return session;
  }

  async function logout(): Promise<void> {
    try {
      await eqsApi.logout();
    } finally {
      clearSession();
    }
  }

  return {
    authenticated: computed(() => Boolean(user.value)),
    access: readonly(access),
    canManageUsers: computed(() => access.value?.canManageUsers === true),
    canViewDirectorBoard: computed(() => access.value?.canViewDirectorBoard === true),
    /*
      升级问题页的入口（0929）。后端按「会不会接到升级」算；浏览器里还留着上一版会话结构时
      这个字段是 undefined，退回原来的口径（总监 / 管理员），不让入口一刷新就消失。
    */
    canViewEscalations: computed(() => access.value?.canViewEscalations === true
      || access.value?.canViewDirectorBoard === true || access.value?.canManageUsers === true),
    canWrite: computed(() => access.value?.canWrite === true),
    canCloseIssues: computed(() => access.value?.canCloseIssues === true),
    canCreateProjects: computed(() => access.value?.canCreateProjects === true),
    ready: readonly(ready),
    user: readonly(user),
    login,
    logout,
  };
}
